import { memo } from "react";

import {
  DEFAULT_ROW_COUNT,
  ROWS_PER_PAGE_OPTIONS,
} from "sj-ui-toolkit/src/constants/tableRowsCounts";

import { Button } from "primereact/button";
import { Card } from "primereact/card";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { Skeleton } from "primereact/skeleton";

import type { NotificationData, TableRow } from "./types";
import { isSkeletonRow } from "./types";

interface NotificationsListCardProps {
  rows: TableRow[];
  selected: NotificationData[];
  onSelectionChange: (value: NotificationData[]) => void;
  onDelete: () => void;
  onEdit: (notification: NotificationData) => void;
  isDeleting: boolean;
}

export const NotificationsListCard = memo(function NotificationsListCard({
  rows,
  selected,
  onSelectionChange,
  onDelete,
  onEdit,
  isDeleting,
}: NotificationsListCardProps) {
  const authorBody = (rowData: TableRow) =>
    isSkeletonRow(rowData) ? <Skeleton width="6rem" height="1.25rem" /> : rowData.author;

  const titleBody = (rowData: TableRow) =>
    isSkeletonRow(rowData) ? <Skeleton width="10rem" height="1.25rem" /> : rowData.title;

  const descriptionBody = (rowData: TableRow) =>
    isSkeletonRow(rowData) ? <Skeleton width="14rem" height="1.25rem" /> : rowData.description;

  const createdAtBody = (rowData: TableRow) =>
    isSkeletonRow(rowData) ? (
      <Skeleton width="8rem" height="1.25rem" />
    ) : (
      new Date(rowData.createdAt).toLocaleString()
    );

  const actionsBody = (rowData: TableRow) =>
    isSkeletonRow(rowData) ? (
      <Skeleton width="3rem" height="1.25rem" />
    ) : (
      <Button label="Edit" link onClick={() => onEdit(rowData)} />
    );

  const isLoading = rows.length > 0 && isSkeletonRow(rows[0]!);

  return (
    <Card>
      <div className="flex flex-col gap-4">
        <DataTable
          header="Previous Notifications"
          aria-label="Previous notifications"
          value={rows}
          dataKey="id"
          selection={isLoading ? [] : selected}
          selectionMode="multiple"
          onSelectionChange={e => onSelectionChange(e.value as NotificationData[])}
          emptyMessage="No notifications found."
          paginator
          rows={DEFAULT_ROW_COUNT}
          rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
          tableClassName="dt-responsive-text"
        >
          <Column selectionMode="multiple" />
          <Column field="author" header="Author" sortable body={authorBody} />
          <Column field="title" header="Title" sortable body={titleBody} />
          <Column field="description" header="Description" body={descriptionBody} />
          <Column field="createdAt" header="Created At" sortable body={createdAtBody} />
          <Column header="Actions" body={actionsBody} />
        </DataTable>

        <Button
          label="Delete Selected"
          icon="pi pi-times"
          severity="danger"
          className="mt-8 self-end"
          disabled={selected.length === 0 || isDeleting}
          loading={isDeleting}
          onClick={onDelete}
        />
      </div>
    </Card>
  );
});
